import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Sparkles, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import KundliPanel from "@/components/KundliPanel";
import ClientKundliHistory from "@/components/ClientKundliHistory";

const SECTIONS = ["Basic", "Astrology", "Kundli", "Consultations", "Payments", "Notes"];

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [insights, setInsights] = useState(null);

  const load = useCallback(async () => {
    try {
      const [clientRes, csRes, psRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get("/consultations", { params: { client_id: id } }),
        api.get("/payments", { params: { client_id: id } }),
      ]);
      setC(clientRes.data);
      setConsultations(csRes.data);
      setPayments(psRes.data);
    } catch (err) { console.error("client load failed:", err?.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await api.patch(`/clients/${id}`, c);
    toast.success("Client updated");
  };

  const remove = async () => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Deleted");
    nav("/clients");
  };

  const generateInsights = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/insights", { client_id: id });
      setInsights(data.insights);
    } catch { toast.error("AI unavailable"); }
    setAiBusy(false);
  };

  if (!c) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => nav("/clients")} data-testid="back-to-clients"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={generateInsights} disabled={aiBusy} data-testid="ai-insights-btn">
            {aiBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1 text-amber-500" />} AI Insights
          </Button>
          <Button variant="outline" onClick={remove} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-client-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
          <Button onClick={save} className="bg-[#1E3A8A] btn-pill" data-testid="save-client-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
        </div>
      </div>

      <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-200 to-amber-400 flex items-center justify-center text-slate-900 text-2xl font-semibold">
            {c.full_name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="font-display text-3xl font-light">{c.full_name}</div>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
              <span className="font-mono text-amber-600">{c.client_code}</span>
              <span>•</span>
              <span>{c.mobile || "—"}</span>
              <span>•</span>
              <span>{c.email || "—"}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Total visits</div>
            <div className="font-mono text-2xl text-slate-800 dark:text-slate-200">{c.total_visits || 0}</div>
          </div>
        </div>
        {insights && (
          <div className="mt-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-sm whitespace-pre-wrap" data-testid="insights-panel">
            <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Insights</div>
            {insights}
          </div>
        )}
      </Card>

      <Tabs defaultValue="Basic">
        <TabsList className="rounded-full">
          {SECTIONS.map(s => <TabsTrigger key={s} value={s} data-testid={`tab-${s.toLowerCase()}`} className="rounded-full">{s}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="Basic" className="mt-6">
          <Card className="p-6 rounded-2xl grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["Full name","full_name"],["Gender","gender"],["DOB","dob","date"],
              ["Time of birth","time_of_birth","time"],["Place of birth","place_of_birth"],
              ["Current address","current_address"],["City","city"],["State","state"],
              ["Country","country"],["Pin code","pin_code"],["Mobile","mobile"],
              ["WhatsApp","whatsapp"],["Email","email"],["Occupation","occupation"],
              ["Company","company"],["Marital status","marital_status"],["Language","language"],
              ["Nationality","nationality"],
              ["Emergency contact name","emergency_contact_name"],
              ["Emergency contact phone","emergency_contact_phone"],
              ["Lead source","lead_source"], ["Referral name","referral_name"],
            ].map(([lbl, k, type]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-widest text-slate-500">{lbl}</Label>
                <Input type={type || "text"} value={c[k] || ""} onChange={e => setC({...c, [k]: e.target.value})} className="mt-1" />
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="Astrology" className="mt-6">
          <Card className="p-6 rounded-2xl grid grid-cols-1 md:grid-cols-3 gap-4">
            {[["Rashi","rashi"],["Nakshatra","nakshatra"],["Lagna","lagna"],["Moon sign","moon_sign"],["Sun sign","sun_sign"],["Mahadasha","mahadasha"],["Antardasha","antardasha"],["Lucky number","lucky_number"],["Lucky colour","lucky_colour"],["Lucky direction","lucky_direction"]].map(([lbl,k]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-widest text-slate-500">{lbl}</Label>
                <Input value={c[k] || ""} onChange={e => setC({...c, [k]: e.target.value})} className="mt-1" />
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="Kundli" className="mt-6 space-y-6">
          <ClientKundliHistory clientId={c.id} refreshTrigger={c.updated_at} />
          <KundliPanel
            initial={{ name: c.full_name, dob: c.dob, tob: c.time_of_birth, place_of_birth: c.place_of_birth }}
            clientId={c.id}
            onSaved={() => api.get(`/clients/${c.id}`).then(r => setC(r.data))}
          />
        </TabsContent>

        <TabsContent value="Consultations" className="mt-6 space-y-3">
          {consultations.length === 0 ? <div className="text-slate-500 text-center py-8">No consultations yet.</div> :
            consultations.map(k => (
              <Card key={k.id} className="p-4 rounded-2xl flex items-center justify-between">
                <div>
                  <div className="font-medium">{k.consultation_type} • <span className="text-slate-500">{k.date}</span></div>
                  <div className="text-sm text-slate-500">{k.problem_description?.slice(0,120)}</div>
                </div>
                <Badge>{k.status}</Badge>
              </Card>
            ))}
        </TabsContent>

        <TabsContent value="Payments" className="mt-6 space-y-3">
          {payments.length === 0 ? <div className="text-slate-500 text-center py-8">No payments recorded.</div> :
            payments.map(p => (
              <Card key={p.id} className="p-4 rounded-2xl flex items-center justify-between">
                <div>
                  <div className="font-mono text-amber-600">{p.invoice_number}</div>
                  <div className="text-sm text-slate-500">{p.payment_method} • {(p.created_at || "").slice(0,10)}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl">₹{p.total?.toLocaleString()}</div>
                  <Badge variant={p.status === "paid" ? "default" : "secondary"}>{p.status}</Badge>
                </div>
              </Card>
            ))}
        </TabsContent>

        <TabsContent value="Notes" className="mt-6">
          <Card className="p-6 rounded-2xl">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Private notes</Label>
            <Textarea rows={8} value={c.notes || ""} onChange={e => setC({...c, notes: e.target.value})} className="mt-2" />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
